import type { FC, HTMLAttributes, ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { DropzoneOptions } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import type { Media } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { getPublicUrl } from 'src/utils/media'
import { uploadMedia } from 'src/utils/upload'
import useLoadingCallback from 'src/utils/useLoadingCallback'

export interface UploadImageProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  value?: Media
  onChange?: (value?: Media) => any
  onUpload?: (uploading: boolean) => any
  onImageSelect?: (file: File) => any
  preHandleImage?: (file: File) => Promise<File> | File
  disabled?: boolean
  placeholderElement?: ReactNode
  dropzoneOptions?: DropzoneOptions

  fileHandleElement?: (state: {
    action: (file?: File) => Promise<void>
  }) => ReactNode
}

const UploadImage: FC<UploadImageProps> = ({
  value: media,
  onChange: setMedia = () => {},
  onUpload = () => {},
  onImageSelect = () => {},
  preHandleImage = i => i,
  className,
  disabled,
  placeholderElement,
  dropzoneOptions,
  fileHandleElement,
  ...props
}) => {
  const { t } = useTranslation(['action'])
  const [file, setFile] = useState<File>()
  const imageUrl = useMemo(() => {
    const url = getPublicUrl(media)
    if (url) return url
    if (file) return URL.createObjectURL(file)
  }, [media, file])

  const [uploadError, setUploadError] = useState<any>()
  const [uploading, upload] = useLoadingCallback(
    async (file: File) => {
      onUpload(true)
      try {
        setUploadError(undefined)
        const res = await uploadMedia(file)
        setMedia(res.media)
        setFile(undefined) // cleanup uploaded file
      } catch (e) {
        console.error(e)
        setUploadError(e)
      } finally {
        onUpload(false)
      }
    },
    [onUpload],
  )

  const onDrop = useCallback(async (files: File[]) => {
    const file = files[0]
    if (file) {
      onImageSelect(file)
      const handledFile = await preHandleImage(file)
      setFile(handledFile)
      upload(handledFile)
    }
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    disabled,
    accept: {
      'image/*': [],
    },
    ...dropzoneOptions,
  })

  const currentFileAction = useCallback(
    async (fileFromArg?: File) => {
      if (!fileFromArg) return
      setFile(fileFromArg)
      upload(fileFromArg)
    },
    [media],
  )

  return (
    <div
      className={cx(
        `
          size-[300px] md:size-[400px]
          border-2 border-dashed rounded-2xl
          flex justify-center items-center
          transition-all hover:transition-all
          text-primary-light font-mono
          `,
        {
          'hover:bg-hover cursor-pointer': !disabled,
        },
        className,
      )}
      {...props}
      {...getRootProps()}
    >
      {imageUrl ? (
        <div className="w-full h-full relative flex items-center overflow-hidden">
          <div
            className="w-full h-full bg-center bg-contain bg-no-repeat rounded-2xl"
            style={{
              backgroundImage: `url(${imageUrl})`,
            }}
          />
          {uploadError ? (
            <div className="absolute inset-0 p-8 bg-black bg-opacity-70 text-white flex flex-col justify-center items-center">
              <span>Failed to upload image. Please try again.</span>
              <span className="whitespace-pre-wrap break-all">
                {uploadError?.message}
              </span>
            </div>
          ) : uploading ? (
            <div className="absolute inset-0 bg-black bg-opacity-70 text-white flex justify-center items-center rounded-2xl">
              uploading...
            </div>
          ) : null}
        </div>
      ) : (
        (placeholderElement ?? (
          <div
            className="text-center font-medium"
            dangerouslySetInnerHTML={{
              __html: t('action:drag-and-drop-hint', { br: '<br />' }) ?? '',
            }}
          ></div>
        ))
      )}
      <input {...getInputProps()} />
      {!uploading && !uploadError
        ? fileHandleElement?.({ action: currentFileAction })
        : null}
    </div>
  )
}

export default UploadImage
